<template>
	<div id="designPalettePage">
		<Navbar tab="" :siteId="navbarSiteId" :site="site" />
		<main v-if="isAdmin" class="palette-page">
			<div class="container">
				<div class="app-page-breadcrumbs">
					<AppBreadcrumbs label="Design Palette" :show-site="false" />
				</div>
				<header class="palette-header">
					<h1>Design Palette</h1>
					<p>
						Vue 2 + Vuetify 2 with HawkenAQ theme tokens.
						Prefer the <strong>Recommended</strong> patterns for new UI.
					</p>
				</header>

				<!-- Colors -->
				<section class="palette-section">
					<h2>Theme tokens</h2>
					<p class="section-note">CSS variables from light/dark themes. Toggle theme from the navbar.</p>
					<div class="swatch-grid">
						<div v-for="token in colorTokens" :key="token" class="swatch">
							<div class="swatch-chip" :style="{ background: `var(${token})` }" />
							<code>{{ token }}</code>
						</div>
					</div>
				</section>

				<!-- Recommended inputs -->
				<section class="palette-section">
					<h2>Recommended — <code>HaqTextField</code> / <code>HaqSelect</code> / …</h2>
					<p class="section-note">
						Standard components under <code>components/form/</code>.
						Use the <code>label</code> prop for an external label — Vuetify floating labels are hidden.
					</p>
					<v-card class="palette-card">
						<v-card-text>
							<v-row>
								<v-col cols="12" md="6">
									<HaqTextField v-model="demo.text" label="Name" />
								</v-col>
								<v-col cols="12" md="6">
									<HaqTextField v-model="demo.password" label="Password" type="password" />
								</v-col>
								<v-col cols="12" md="6">
									<HaqSelect
										v-model="demo.select"
										:items="selectOptions"
										label="Status"
										clearable
									/>
								</v-col>
								<v-col cols="12" md="6">
									<HaqAutocomplete
										v-model="demo.autocomplete"
										:items="selectOptions"
										label="Site"
										clearable
									/>
								</v-col>
								<v-col cols="12" md="6">
									<HaqTextField
										v-model="demo.search"
										label="Search"
										append-icon="mdi-magnify"
									/>
								</v-col>
								<v-col cols="12" md="6" class="d-none d-md-flex" />
								<v-col cols="12">
									<HaqTextarea v-model="demo.textarea" label="Notes" rows="3" />
								</v-col>
							</v-row>
						</v-card-text>
					</v-card>
				</section>

				<!-- Legacy haq-text-field — disallowed for new UI -->
				<section class="palette-section palette-section--disallowed">
					<h2>
						<span class="disallowed-badge">Disallowed</span>
						Legacy — <code>haq-text-field</code>
					</h2>
					<p class="section-note">
						Do <strong>not</strong> use for new work. Exists only for older screens (Device modal, Settings, etc.).
						Use <code>HaqTextField</code> / <code>HaqSelect</code> instead.
					</p>
					<v-card class="palette-card palette-card--disallowed">
						<v-card-text>
							<v-row>
								<v-col cols="12" md="6">
									<label class="field-label">Text</label>
									<v-text-field
										v-model="demo.legacyText"
										label="Device Name"
										class="haq-text-field"
									/>
								</v-col>
								<v-col cols="12" md="6">
									<label class="field-label">Select</label>
									<v-select
										v-model="demo.legacySelect"
										:items="selectOptions"
										label="Zone"
										class="haq-text-field"
									/>
								</v-col>
								<v-col cols="12" md="6">
									<label class="field-label">Disabled</label>
									<v-text-field
										value="Read only value"
										label="Serial Number"
										class="haq-text-field"
										disabled
									/>
								</v-col>
							</v-row>
						</v-card-text>
					</v-card>
				</section>

				<!-- Raw Vuetify — disallowed for new UI -->
				<section class="palette-section palette-section--disallowed">
					<h2>
						<span class="disallowed-badge">Disallowed</span>
						Raw Vuetify (unthemed)
					</h2>
					<p class="section-note">
						Do <strong>not</strong> use bare <code>v-text-field</code> / <code>v-select</code> without Haq wrappers.
						Colors and density will not match the app.
					</p>
					<v-card class="palette-card palette-card--disallowed">
						<v-card-text>
							<v-row>
								<v-col cols="12" md="6">
									<v-text-field v-model="demo.rawText" label="Default outlined" outlined dense />
								</v-col>
								<v-col cols="12" md="6">
									<v-text-field v-model="demo.rawFilled" label="Default filled" filled dense />
								</v-col>
							</v-row>
						</v-card-text>
					</v-card>
				</section>

				<!-- Toggles -->
				<section class="palette-section">
					<h2>Toggles &amp; choice</h2>
					<v-card class="palette-card">
						<v-card-text>
							<v-row align="center">
								<v-col cols="12" md="4">
									<v-checkbox v-model="demo.checkbox" label="Checkbox" hide-details class="mt-0" />
								</v-col>
								<v-col cols="12" md="4">
									<v-switch v-model="demo.switch" label="Switch" hide-details class="mt-0" />
								</v-col>
								<v-col cols="12" md="4">
									<v-radio-group v-model="demo.radio" row hide-details class="mt-0">
										<v-radio label="A" value="a" />
										<v-radio label="B" value="b" />
									</v-radio-group>
								</v-col>
							</v-row>
						</v-card-text>
					</v-card>
				</section>

				<!-- Buttons -->
				<section class="palette-section">
					<h2>Buttons</h2>
					<v-card class="palette-card">
						<v-card-text class="button-row">
							<v-btn class="haq-btn haq-btn-primary">Primary</v-btn>
							<v-btn class="haq-btn haq-btn-secondary">Secondary</v-btn>
							<v-btn class="haq-btn haq-btn-primary" text>Text primary</v-btn>
							<v-btn text>Plain text</v-btn>
							<v-btn class="haq-btn haq-btn-primary" disabled>Disabled</v-btn>
							<span class="nav-new-badge">New</span>
						</v-card-text>
					</v-card>
				</section>

				<!-- Dialog -->
				<section class="palette-section">
					<h2>Dialog — <code>haq-dialog</code></h2>
					<p class="section-note">
						<code>v-dialog</code> with <code>content-class="haq-dialog"</code> and
						<code>v-card.haq-dialog-card</code>.
					</p>
					<v-btn class="haq-btn haq-btn-primary" @click="dialogOpen = true">Open sample dialog</v-btn>
					<v-dialog v-model="dialogOpen" content-class="haq-dialog" max-width="520">
						<v-card class="haq-dialog-card">
							<v-card-title>Sample dialog</v-card-title>
							<v-card-text>
								<HaqTextField v-model="demo.dialogText" label="Name" />
							</v-card-text>
							<v-card-actions>
								<v-spacer />
								<v-btn text @click="dialogOpen = false">Cancel</v-btn>
								<v-btn class="haq-btn haq-btn-primary" @click="dialogOpen = false">Save</v-btn>
							</v-card-actions>
						</v-card>
					</v-dialog>
				</section>

				<!-- Table -->
				<section class="palette-section">
					<h2>Data table — <code>haq-data-table</code></h2>
					<v-data-table
						class="elevation-8 haq-data-table"
						:headers="tableHeaders"
						:items="tableItems"
						:search="demo.tableSearch"
						:items-per-page="5"
						item-key="id"
					>
						<template v-slot:top>
							<div class="table-search">
								<HaqTextField
									v-model="demo.tableSearch"
									label="Search"
									append-icon="mdi-magnify"
								/>
							</div>
						</template>
					</v-data-table>
				</section>

				<!-- Usage guide -->
				<section class="palette-section">
					<h2>When to use what</h2>
					<v-card class="palette-card">
						<v-card-text>
							<ul class="guide-list">
								<li><strong>Allowed:</strong> <code>HaqTextField</code> / <code>HaqSelect</code> / <code>HaqAutocomplete</code> / <code>HaqTextarea</code> — pass <code>label</code> for an external label</li>
								<li><strong>Allowed:</strong> <code>haq-dialog</code> / <code>haq-dialog-card</code>, <code>haq-btn haq-btn-primary</code>, <code>haq-data-table</code></li>
								<li><strong>Disallowed:</strong> <code>haq-text-field</code> class on raw Vuetify inputs — legacy only</li>
								<li><strong>Disallowed:</strong> unthemed / raw Vuetify form controls for new UI</li>
							</ul>
						</v-card-text>
					</v-card>
				</section>
			</div>
		</main>
	</div>
</template>

<script>
import Navbar from '@/components/Navbar.vue';
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue';
import HaqTextField from '@/components/form/HaqTextField.vue';
import HaqSelect from '@/components/form/HaqSelect.vue';
import HaqAutocomplete from '@/components/form/HaqAutocomplete.vue';
import HaqTextarea from '@/components/form/HaqTextarea.vue';
import { HTTPClient } from '../assets/js/http';
import { firstAccessibleSite, isAccessibleSiteId, sitesUserCanAccess } from '../assets/js/siteAccess';

let client = new HTTPClient();

export default {
	name: 'DesignPalette',
	components: {
		Navbar,
		AppBreadcrumbs,
		HaqTextField,
		HaqSelect,
		HaqAutocomplete,
		HaqTextarea,
	},
	data() {
		return {
			site: null,
			dialogOpen: false,
			selectOptions: ['Active', 'Disabled', 'Revoked', 'Pending'],
			colorTokens: [
				'--app-primary-bg',
				'--app-secondary-bg',
				'--app-tertiary-bg',
				'--app-primary-txt',
				'--app-tertiary-txt',
				'--input-bg',
				'--input-txt',
				'--btn-primary-bg',
				'--btn-secondary-bg',
				'--score-blue-color',
				'--primary-alert',
				'--border',
			],
			demo: {
				text: '',
				password: '',
				select: null,
				autocomplete: null,
				textarea: '',
				legacyText: '',
				legacySelect: null,
				search: '',
				rawText: '',
				rawFilled: '',
				checkbox: true,
				switch: false,
				radio: 'a',
				dialogText: '',
				tableSearch: '',
			},
			tableHeaders: [
				{ text: 'Name', value: 'name' },
				{ text: 'Status', value: 'status' },
				{ text: 'Updated', value: 'updated' },
			],
			tableItems: [
				{ id: 1, name: 'Edge Alpha', status: 'active', updated: '2h ago' },
				{ id: 2, name: 'Edge Beta', status: 'disabled', updated: '1d ago' },
				{ id: 3, name: 'Edge Gamma', status: 'active', updated: '3d ago' },
			],
		};
	},
	computed: {
		/**
		 * True when the current user is an admin or superAdmin.
		 * @returns {boolean}
		 */
		isAdmin() {
			const role = this.resolveUser()?.role;
			return role === 'admin' || role === 'superAdmin';
		},
		/**
		 * Site id for navbar context.
		 * @returns {string|null}
		 */
		navbarSiteId() {
			return this.site?.objectId || null;
		},
	},
	methods: {
		/**
		 * Resolve the current user from the store or localStorage.
		 * @returns {Object|null}
		 */
		resolveUser() {
			let user = this.$store.state.user;
			if (user) return user;
			try {
				const raw = localStorage.getItem('user');
				if (!raw) return null;
				user = JSON.parse(raw);
				this.$store.commit('setUser', user);
				return user;
			} catch (error) {
				return null;
			}
		},
		/**
		 * Redirect non-admins away once the user record is known.
		 * @returns {boolean} true when the page may continue loading
		 */
		guardAdmin() {
			const user = this.resolveUser();
			if (!user) return false;
			if (user.role !== 'admin' && user.role !== 'superAdmin') {
				this.$router.replace('/');
				return false;
			}
			return true;
		},
		/**
		 * Load a site into navbar context so nav links render.
		 */
		async loadSiteContext() {
			try {
				let sites = this.$store.state.sites;
				if (!sites || sites.length === 0) {
					const res = await client.robustQuery('/api', { slim: true });
					const pid = this.$store.state.project?.objectId;
					sites = sitesUserCanAccess(res.sites || [], this.$store.state.user, pid);
					this.$store.commit('setSites', sites);
				}
				const leftover = this.$store.state.site;
				const accessible = this.$store.getters.accessibleSites || [];
				const leftoverId = leftover ? (leftover.objectId || leftover.id) : null;
				if (leftover && isAccessibleSiteId(leftoverId, accessible)) {
					this.site = leftover;
					return;
				}
				const first = this.$store.getters.firstAccessibleSite || firstAccessibleSite(sites, this.$store.state.user, this.$store.state.project?.objectId);
				if (first) {
					this.site = {
						objectId: first.objectId,
						name: first.name,
						commanderLicenseId: first.commanderLicenseId || null,
						project: first.project || null,
						canBeScheduled: first.canBeScheduled || false,
					};
				}
			} catch (error) {
				console.error('Failed to load site context', error);
			}
		},
	},
	mounted() {
		if (!this.guardAdmin()) return;
		this.loadSiteContext();
	},
	watch: {
		/**
		 * Re-check admin access when the store user arrives after mount.
		 * @param {Object|null} user
		 */
		'$store.state.user'(user) {
			if (!user) return;
			if (!this.guardAdmin()) return;
			if (!this.site) this.loadSiteContext();
		},
	},
};
</script>

<style lang="scss" scoped>
.palette-page {
	padding-bottom: 64px;
	background: var(--app-primary-bg);
	min-height: calc(100vh - 64px);
}

.palette-header {
	margin: 16px 0 32px;
	h1 {
		color: var(--app-primary-txt);
		font-size: 28px;
		margin: 0 0 8px;
	}
	p {
		color: var(--app-primary-txt);
		opacity: 0.8;
		max-width: 720px;
		margin: 0;
	}
}

.palette-section {
	margin-bottom: 40px;
	h2 {
		color: var(--app-primary-txt);
		font-size: 20px;
		margin: 0 0 8px;
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px;
		code {
			font-size: 0.85em;
			background: var(--app-tertiary-bg);
			padding: 2px 6px;
			border-radius: 4px;
		}
	}
	&.palette-section--disallowed {
		opacity: 0.85;
		h2 {
			color: var(--app-primary-txt);
		}
	}
}

.disallowed-badge {
	display: inline-flex;
	align-items: center;
	height: 22px;
	padding: 0 8px;
	border-radius: 4px;
	background: #fa5f5f;
	color: #fff;
	font-size: 11px;
	font-weight: 700;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	line-height: 1;
}

.section-note {
	color: var(--app-primary-txt);
	opacity: 0.7;
	margin: 0 0 16px;
	max-width: 720px;
	code {
		font-size: 0.9em;
		background: var(--app-tertiary-bg);
		padding: 1px 5px;
		border-radius: 3px;
	}
}

.palette-card {
	background: var(--app-secondary-bg) !important;
	color: var(--app-primary-txt);
	border-radius: 20px !important;
	box-shadow: var(--box-shadow);
	&.palette-card--disallowed {
		outline: 1px dashed rgba(250, 95, 95, 0.55);
		outline-offset: 0;
	}
}

.field-label {
	display: block;
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: var(--app-tertiary-txt);
	opacity: 0.7;
	margin-bottom: 0;
	margin-top: 12px;
}

.table-search {
	max-width: 320px;
	margin: 8px 0 12px;
}

.swatch-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
	gap: 12px;
}

.swatch {
	background: var(--app-secondary-bg);
	border-radius: 12px;
	padding: 10px;
	box-shadow: var(--box-shadow);
	.swatch-chip {
		height: 48px;
		border-radius: 8px;
		border: 1px solid var(--border);
		margin-bottom: 8px;
	}
	code {
		font-size: 11px;
		color: var(--app-primary-txt);
		word-break: break-all;
	}
}

.button-row {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 12px;
}

.nav-new-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 18px;
	padding: 0 7px;
	border-radius: 4px;
	background: #0098ff;
	color: #fff;
	font-size: 10px;
	font-weight: 700;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	line-height: 1;
}

.guide-list {
	margin: 0;
	padding-left: 20px;
	color: var(--app-primary-txt);
	li {
		margin-bottom: 8px;
	}
}
</style>
